import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addAboutItemToLast, pushToVersions, setSectionFormType, updateVersionIndex } from '../../../../../Features/storefrontSlice';
import CrossGray from '../../../../../Assets/StorefrontIcons/CrossGray.svg';
import FormInput from '../AboutOneForm/FormInput';
import ImageInput from './ImageInput';
import { StorefrontContext } from '../../../../../Context/StorefrontContext';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { getMediaUrl } from '../../../../../lib/utils/utils';

function AboutThreeForm() {
    const dispatch = useDispatch();

    const versionStack = useSelector((state) => state.storefront.versionStack);
    const versionIndex = useSelector((state) => state.storefront.versionIndex);

    const {
        defaultAboutThree,
        aboutThree,
        setAboutThree,
    } = useContext(StorefrontContext);

    const defaultFormErrors = {
        altText: '',
        heading: '',
        description: '',
        image: '',
    }

    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    function handleChange(e) {
        const { name, value } = e.target;
        setAboutThree({ ...aboutThree, [name]: value });
    }

    function closeForm() {
        dispatch(setSectionFormType(null));
        setAboutThree(defaultAboutThree);
    }

    async function handleSave() {
        const validationErrors = {};

        if(!aboutThree.altText) {
            validationErrors.altText = 'Required field';
        }

        if(!aboutThree.heading) {
            validationErrors.heading = 'Required field';
        }

        if(!aboutThree.description) {
            validationErrors.description = 'Required field';
        }

        if(Object.keys(validationErrors).length === 0) {
            setFormErrors(defaultFormErrors);

            const object = {
                TypeData: "ABOUT3",
                altText: aboutThree.altText,
                Heading: aboutThree.heading,
                Description: aboutThree.description,
            }

            const currentVersion = versionStack[versionIndex]

            const tempVersion = currentVersion?.storeAbout;

            const image = aboutThree.image.file;

            if (image) {
                let url;
        
                if (image.size > 1048576) {
                    try {
                        const compressedBlob = await imageCompression(image, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
                        const compressedFile = new File([compressedBlob], image.name, { type: image.type, lastModified: Date.now() });

                        url = await getMediaUrl(compressedFile);
                    }
                    catch (err) {
                        console.warn('Image compression failed, using original file:', err);
                        url = await getMediaUrl(image);
                    }
                }
                else {
                    url = await getMediaUrl(image);
                }
                object.imageLink = url;
            }
            else {
                object.imageLink = aboutThree.image.url || "";
            }

            const aboutThreeDataIndex = tempVersion.findIndex(item => item.item.TypeData === 'ABOUT3');

            const tempVersionData = [ ...tempVersion ];

            if (aboutThreeDataIndex !== -1) {
                tempVersionData[aboutThreeDataIndex] = {
                    ...tempVersionData[aboutThreeDataIndex],
                    item: object
                };

                const newIndex = versionStack[versionStack.length - 1].index + 1;

                const newVersion = {
                    ...currentVersion,
                    index: newIndex,
                    storeAbout: tempVersionData,
                    comment: 'Update About3 section',
                }

                dispatch(pushToVersions(newVersion));
                dispatch(updateVersionIndex(newIndex));
            }
            else {
                dispatch(addAboutItemToLast(object));
            }
            closeForm();
        }
        else {
            setFormErrors(validationErrors);
            return;
        }
    }

    useEffect(() => {
        function fetchData() {
            const tempVersion = versionStack[versionIndex]?.storeAbout;

            if(tempVersion.length > 0) {
                const { item: aboutOneData = null } = tempVersion.find(item => item.item.TypeData === 'ABOUT3') || {};

                if(aboutOneData) {
                    setAboutThree({
                        altText: aboutOneData.altText,
                        heading: aboutOneData.Heading,
                        description: aboutOneData.Description,
                        image: {
                            id: uuidv4(),
                            url: aboutOneData.imageLink,
                            file: null,
                        },
                    })
                }
            }
        }

        fetchData();
        // eslint-disable-next-line
    }, [versionStack, versionIndex]);

    return (
        <div className='w-[305px] h-full bg-[#FFFFFF] border-l border-[#EAEAEA] flex flex-col gap-4 select-none relative'>
            <div className="flex items-center justify-between">
                <p className='font-semibold text-[20px] leading-6 text-[#121212] pl-3'>About 3</p>

                <div className="size-11 p-[10px] flex items-center justify-center">
                    <button onClick={closeForm} className='size-6 flex items-center justify-center outline-none'>
                        <img src={CrossGray} alt="" />
                    </button>
                </div>
            </div>

            <div className="h-[calc(100%-44px)] flex flex-col gap-3 px-3 overflow-y-scroll no-scrollbar pb-16">
                <ImageInput />

                <FormInput 
                    errorText={formErrors.altText} 
                    inputOptions={{
                        name: "altText",
                        id: "altText",
                        placeholder: 'Describe your image for people with visual impairments',
                        value: aboutThree.altText,
                        onChange: handleChange,
                    }} 
                    title='Alt Text' 
                />

                <FormInput 
                    errorText={formErrors.heading} 
                    inputOptions={{
                        name: "heading",
                        id: "heading",
                        placeholder: 'Write heading here',
                        value: aboutThree.heading,
                        onChange: handleChange,
                    }} 
                    title='Heading' 
                />

                <FormInput 
                    errorText={formErrors.description} 
                    inputOptions={{
                        name: "description",
                        id: "description",
                        placeholder: 'Write description here',
                        value: aboutThree.description,
                        onChange: handleChange,
                    }} 
                    title='Description' 
                />
            </div>

            <div className="absolute bottom-0 left-0 w-full p-3 bg-[#FFFFFF]">
                <button onClick={handleSave} className='w-full outline-none px-4 py-[6px] bg-primaryColor hover:bg-primaryAccent rounded-md font-semibold text-[12px] leading-6 text-buttonText'>Save changes</button>
            </div>
        </div>
    );
}

export default AboutThreeForm;
